import { create } from 'zustand';
import { Devis, StatutDevis, StatutProjet } from '../types';
import { extractErrorMessage } from '../utils/errorUtils';
import devisService from '../services/devisService';
import { normalizeDevis } from '../utils/calculations';
import { useDashboardStore } from './useDashboardStore';
import { useProjetStore } from './useProjetStore';
import projetService from '../services/projetService';

/**
 * Helper function to sync project status based on devis status
 * and reload projects to ensure UI is up-to-date
 */
const syncProjetStatusAndReload = async (projetId: number, devisStatut: StatutDevis): Promise<void> => {
  try {
    // Déterminer le nouveau statut du projet en fonction du statut du devis
    let newProjetStatut: StatutProjet | null = null;

    switch (devisStatut) {
      case StatutDevis.ENVOYE:
        newProjetStatut = StatutProjet.DEVIS_ENVOYE;
        break;
      case StatutDevis.ACCEPTE:
        newProjetStatut = StatutProjet.DEVIS_ACCEPTE;
        break;
      case StatutDevis.REFUSE:
        newProjetStatut = StatutProjet.EN_ATTENTE_DEVIS;
        break;
      case StatutDevis.BROUILLON:
        // Ne rien faire pour un brouillon
        return;
    }

    // Mettre à jour le statut du projet si nécessaire
    if (newProjetStatut) {
      console.log(`🔄 Mise à jour du statut du projet ${projetId} vers ${newProjetStatut}`);
      await projetService.updateStatus(projetId, newProjetStatut);
      console.log(`✅ Statut du projet ${projetId} mis à jour`);
    }

    // Recharger les projets pour mettre à jour l'interface
    const { loadProjets } = useProjetStore.getState();
    await loadProjets();
    console.log('✅ Projets rechargés');
  } catch (error) {
    console.error('⚠️ Erreur lors de la synchronisation du statut du projet:', error);
    // Ne pas bloquer l'opération principale en cas d'erreur
  }
};

interface QuoteStore {
  devis: Devis[];
  isLoading: boolean;
  error: string | null;
  loadDevis: () => Promise<void>;
  loadDevisById: (id: number) => Promise<Devis | null>;
  addDevis: (projetId: number, devis: Omit<Devis, 'id' | 'numero' | 'createdAt' | 'updatedAt'>) => Promise<Devis>;
  updateDevis: (id: number, devis: Partial<Devis>) => Promise<void>;
  deleteDevis: (id: number) => Promise<void>;
  getDevisById: (id: number) => Devis | undefined;
  getDevisByClientId: (clientId: number) => Devis[];
  loadDevisByClientId: (clientId: number) => Promise<void>;
  duplicateDevis: (id: number) => Promise<void>;
  updateDevisStatus: (id: number, statut: StatutDevis) => Promise<void>;
  saveSignature: (id: number, signatureData: string, dateSignature: string) => Promise<void>;
  clearDevis: () => void;
}

export const useQuoteStore = create<QuoteStore>((set, get) => ({
  devis: [],
  isLoading: false,
  error: null,

  // Charger tous les devis depuis l'API
  loadDevis: async () => {
    set({ isLoading: true, error: null });
    try {
      const devis = await devisService.getAll();
      // Normaliser tous les devis pour s'assurer que les valeurs numériques sont valides
      const normalizedDevis = devis.map(d => normalizeDevis(d));
      set({ devis: normalizedDevis, isLoading: false });
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des devis');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Charger un devis spécifique depuis l'API
  loadDevisById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const devis = await devisService.getById(id);
      console.log('🔍 Devis chargé dans le store:', devis);
      console.log('🔍 Type de devis.id:', typeof devis.id);

      // Normaliser le devis avant de le stocker
      const normalizedDevis = normalizeDevis(devis);

      // Ajouter ou mettre à jour le devis dans le store
      set((state) => {
        const existingIndex = state.devis.findIndex(d => d.id === id);
        console.log('🔍 Index existant:', existingIndex);
        let updatedDevis;
        if (existingIndex >= 0) {
          // Mettre à jour le devis existant
          updatedDevis = [...state.devis];
          updatedDevis[existingIndex] = normalizedDevis;
        } else {
          // Ajouter le nouveau devis
          updatedDevis = [...state.devis, normalizedDevis];
        }
        console.log('🔍 Store mis à jour, nombre de devis:', updatedDevis.length);
        return { devis: updatedDevis, isLoading: false };
      });
      return normalizedDevis;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement du devis');
      set({ error: errorMessage, isLoading: false });
      return null;
    }
  },

  // Ajouter un devis via l'API
  addDevis: async (projetId, devisData) => {
    set({ isLoading: true, error: null });
    try {
      console.log('📤 Données frontend:', JSON.stringify(devisData, null, 2));
      const newDevis = await devisService.create(projetId, devisData);
      console.log('✅ Devis créé avec succès:', newDevis);
      // Normaliser le devis avant de l'ajouter au store
      const normalizedDevis = normalizeDevis(newDevis);
      set((state) => ({
        devis: [...state.devis, normalizedDevis],
        isLoading: false,
      }));
      // Invalider le cache du dashboard
      useDashboardStore.getState().invalidateCache();

      // Synchroniser le statut du projet avec le statut du devis
      await syncProjetStatusAndReload(projetId, normalizedDevis.statut);

      // Retourner le devis créé pour la redirection
      return normalizedDevis;
    } catch (error: any) {
      console.error('❌ Erreur complète:', error);
      console.error('❌ Réponse du serveur:', error.response?.data);
      console.error('❌ Statut:', error.response?.status);
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création du devis');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Mettre à jour un devis via l'API
  updateDevis: async (id, devisData) => {
    set({ isLoading: true, error: null });
    try {
      console.log('🔄 Mise à jour du devis:', id, 'avec signature:', !!devisData.signatureClient);
      const updatedDevis = await devisService.update(id, devisData);
      console.log('📥 Devis retourné par l\'API:', {
        id: updatedDevis.id,
        statut: updatedDevis.statut,
        hasSignature: !!updatedDevis.signatureClient,
        dateSignature: updatedDevis.dateSignature,
      });
      // Normaliser le devis après la mise à jour
      const normalizedDevis = normalizeDevis(updatedDevis);
      console.log('✅ Devis normalisé:', {
        id: normalizedDevis.id,
        hasSignature: !!normalizedDevis.signatureClient,
        dateSignature: normalizedDevis.dateSignature,
      });
      set((state) => ({
        devis: state.devis.map((devis) =>
          devis.id === id ? normalizedDevis : devis
        ),
        isLoading: false,
      }));
      // Invalider le cache du dashboard
      useDashboardStore.getState().invalidateCache();

      // Synchroniser le statut du projet si le statut a changé
      if (normalizedDevis.projetId && devisData.statut) {
        await syncProjetStatusAndReload(normalizedDevis.projetId, normalizedDevis.statut);
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour du devis');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Supprimer un devis via l'API
  deleteDevis: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await devisService.delete(id);
      set((state) => ({
        devis: state.devis.filter((devis) => devis.id !== id),
        isLoading: false,
      }));
      // Invalider le cache du dashboard
      useDashboardStore.getState().invalidateCache();
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la suppression du devis');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Récupérer un devis par ID (depuis le state local)
  getDevisById: (id) => {
    return get().devis.find((devis) => devis.id === id);
  },

  // Récupérer tous les devis d'un client (depuis le state local)
  getDevisByClientId: (clientId) => {
    return get().devis.filter((devis) => devis.clientId === clientId);
  },

  // Charger les devis d'un client depuis l'API
  loadDevisByClientId: async (clientId) => {
    set({ isLoading: true, error: null });
    try {
      const devis = await devisService.getByClientId(clientId);
      // Normaliser tous les devis avant de les ajouter
      const normalizedDevis = devis.map(d => normalizeDevis(d));
      // Merge avec les devis existants pour éviter la perte de données
      set((state) => ({
        devis: [
          ...state.devis.filter(d => d.clientId !== clientId),
          ...normalizedDevis
        ],
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des devis du client');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Dupliquer un devis via l'API
  duplicateDevis: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const duplicatedDevis = await devisService.duplicate(id);
      // Normaliser le devis dupliqué
      const normalizedDevis = normalizeDevis(duplicatedDevis);
      set((state) => ({
        devis: [...state.devis, normalizedDevis],
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la duplication du devis');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Mettre à jour le statut d'un devis via l'API
  updateDevisStatus: async (id, statut) => {
    set({ isLoading: true, error: null });
    try {
      const updatedDevis = await devisService.updateStatus(id, statut);
      // Normaliser le devis après la mise à jour du statut
      const normalizedDevis = normalizeDevis(updatedDevis);
      set((state) => ({
        devis: state.devis.map((devis) =>
          devis.id === id ? normalizedDevis : devis
        ),
        isLoading: false,
      }));

      // Synchroniser le statut du projet
      if (normalizedDevis.projetId) {
        await syncProjetStatusAndReload(normalizedDevis.projetId, statut);
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour du statut');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Sauvegarder la signature du client
  saveSignature: async (id, signatureData, dateSignature) => {
    set({ isLoading: true, error: null });
    try {
      console.log('💾 Store: sauvegarde de la signature pour le devis', id);
      const updatedDevis = await devisService.saveSignature(id, signatureData, dateSignature);
      console.log('📥 Store: devis retourné:', {
        id: updatedDevis.id,
        hasSignature: !!updatedDevis.signatureClient,
        dateSignature: updatedDevis.dateSignature,
      });
      // Normaliser le devis après la sauvegarde de la signature
      const normalizedDevis = normalizeDevis(updatedDevis);
      set((state) => ({
        devis: state.devis.map((devis) =>
          devis.id === id ? normalizedDevis : devis
        ),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde de la signature:', error);
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la sauvegarde de la signature');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Vider tous les devis (lors du logout)
  clearDevis: () => {
    set({ devis: [], isLoading: false, error: null });
  },
}));